import React, { Suspense, memo, useCallback, useState, useMemo } from "react";
import loadable from "@loadable/component";
import { shallowEqual } from "react-redux";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import styles from "./GradeSwitch.module.scss";
import Icon from "../../../../../shared-components/css-modules/lexus/atoms/Icon";
import Container from "../../../../../shared-components/css-modules/lexus/atoms/Container";
import { useCarConfigSelector } from "../../../../redux/store";
import Popover from "../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import { trackOpenGradeList, trackSelectGrade } from "../../../../utils/tracking";
import {
    CarConfigStep,
    getCarConfigUrl,
} from "../../../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import GradeSwitcherSkeleton from "../../../../../shared-components/css-modules/lexus/organisms/GradeSwitcherSkeleton";
import { getCurrentNavQuerySelector } from "../../../../utils/selectorUtils";
import { getUniqueGradeBodyTypeId } from "../../../../../../../common-deprecated/features/apheleia/utils/aphUtils";
import a11y from "../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { CarConfigLabel } from "../../../../utils/labelUtils";

const IntegratedGradeSwitcher = loadable(async () => {
    const component = await import(
        /* webpackChunkName: "integrated-grade-switcher-lexus" */
        "../../../../../grade-selector/components/lexus/IntegratedGradeSwitcher"
    );
    return component.default;
});

const GradeSwitch: React.FC = () => {
    const car = useCarConfigSelector((state) => state.car);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const section = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const colourId = useCarConfigSelector((state) => state.basket.colour.id);
    const motorization = useCarConfigSelector((state) => state.car.motorization);

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const activeStep = useCarConfigSelector(getCurrentNavQuerySelector, shallowEqual)?.step;

    const onChangeListen = useCallback(
        (open: boolean) => {
            if (open && section) {
                trackOpenGradeList(section);
            }
        },
        [section],
    );

    const onSelect = useCallback(
        (carId: string, name: string, selectedColourId: string, selectedColourCode: string, carExtras: string[]) => {
            if (section) {
                trackSelectGrade(name, section);
            }

            window.location.href = getCarConfigUrl(
                commonSettings,
                car.model.id,
                car.model.internalCode,
                carId,
                activeStep ?? CarConfigStep.Configure,
                { colourCode: selectedColourCode, colourId: selectedColourId },
                undefined,
                undefined,
                carExtras,
            );
        },
        [section, commonSettings, car.model.id, car.model.internalCode, activeStep],
    );

    const onPopoverTriggerClick = useCallback(() => {
        setIsOpen((oldIsOpen) => !oldIsOpen);
    }, []);

    const onKeep = useCallback(() => {
        setIsOpen(false);
    }, []);

    return useMemo(
        () => (
            <>
                <Popover
                    placement="bottom-start"
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    onOpenChangeListen={onChangeListen}
                    modal
                >
                    <Popover.Trigger>
                        <Button
                            variant="clean"
                            sizing="pill"
                            onClick={onPopoverTriggerClick}
                            className={styles.gradeSelector}
                        >
                            <CarConfigLabel label="carConfigChangeGrade" useHTML />
                            <Icon variant="chevron-down" sizing="sm" role="menu" aria-hidden="true" />
                        </Button>
                    </Popover.Trigger>

                    <Popover.Content variant="grade-switcher">
                        <Container className={styles.container}>
                            <div className={styles.wrapper}>
                                <Popover.Close>
                                    <Button variant="clean" className={styles.close}>
                                        <Icon variant="close-sm" sizing="sm" aria-hidden="true" />
                                        <span className={a11y["sr-only"]}>
                                            <CarConfigLabel label="carConfigTooltipClose" />
                                        </span>
                                    </Button>
                                </Popover.Close>
                                <Suspense fallback={<GradeSwitcherSkeleton />}>
                                    <IntegratedGradeSwitcher
                                        modelId={car.model.id}
                                        selectedGradeBodyId={getUniqueGradeBodyTypeId(
                                            car.grade.id,
                                            car.body.id,
                                            car.submodel?.id,
                                        )}
                                        selectedMotorization={motorization}
                                        selectedColourId={colourId}
                                        onSelect={onSelect}
                                        onKeep={onKeep}
                                    />
                                </Suspense>
                            </div>
                        </Container>
                    </Popover.Content>
                </Popover>
            </>
        ),
        [isOpen, car],
    );
};

export default memo(GradeSwitch);
